//@ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import Spinner from '../../components/bootstrap/Spinner';
import pdfServiceStore from '../mobx/pdfState';
import { observer } from 'mobx-react';
import auth from '../../shared/mobx/auth-state';
import NewsLetter from './news-letter';
import StableSvgLoader from './StableSvgLoader';

const PdfProgress = ({ conversionType, showProgressText, notify, handleStartOver }) => {
    const [progressText, setProgressText] = useState("Loading webpage...");
    const [showSvgLoader, setShowSvgLoader] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);
    
    // Move messages arrays inside useEffect to avoid dependency warnings
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const messages = React.useMemo(() => [
        {
            text: conversionType === "current_page" ? `Waiting for a webpage to load` : `Waiting for webpages to load`,
            progress: 0,
        },
        {
            text: "Preparing content...",
            progress: 25,
        },
        {
            text: "Converting to PDF...",
            progress: 50,
        },
        {
            text: "Optimizing document...",
            progress: 75,
        },
        {
            text: "Almost there...",
            progress: 90,
        },
    ], [conversionType]);

    const middleMessages = React.useMemo(() => [
        {
            text: "Accessing URL..."
        },
        {
            text: "Scanning all URL content..."
        },
        {
            text: "Analyzing document..."
        },
        {
            text: "Analyzing object coverage..."
        },
        {
            text: "Optimizing indirect objects..."
        },
        {
            text: "Setting page layout..."
        },
        {
            text: "Adjusting conversion settings..."
        },
        {
            text: "Converting..."
        },
        {
            text: "Finalizing..."
        },
    ], []);

    const progressTextRef = useRef(progressText);
    const middleMessageIndexRef = useRef(0);
    const lastMiddleMessageTimeRef = useRef(0);

    useEffect(() => {
        progressTextRef.current = progressText;
    }, [progressText]);

    useEffect(() => {
        // Stop counter immediately if PDF is completed or failed, regardless of progress
        if (!pdfServiceStore.isLoading || pdfServiceStore.status === 'complete' || pdfServiceStore.status === 'failed') {
            // Reset when loading stops or PDF is complete
            middleMessageIndexRef.current = 0;
            lastMiddleMessageTimeRef.current = 0;
            // If PDF is complete, set progress to 100 to trigger completion UI
            if (pdfServiceStore.status === 'complete' && pdfServiceStore.progress < 100) {
                pdfServiceStore.progress = 100;
            }
            return;
        }

        // Reset middle message index when loading starts
        middleMessageIndexRef.current = 0;
        lastMiddleMessageTimeRef.current = 0;

        let interval;
        let lastProgressUpdateTime = Date.now();
        
        // Use a consistent interval that checks timing internally
        interval = setInterval(() => {
            // Stop immediately if PDF is completed or failed
            if (pdfServiceStore.status === 'complete' || pdfServiceStore.status === 'failed') {
                clearInterval(interval);
                if (pdfServiceStore.status === 'complete' && pdfServiceStore.progress < 100) {
                    pdfServiceStore.progress = 100;
                }
                return;
            }

            if (pdfServiceStore.progress >= 100 && !pdfServiceStore.isLoading) {
                clearInterval(interval);
                return;
            }

            const now = Date.now();

            if (pdfServiceStore.progress < 100) {
                // Update progress every 100ms
                if (now - lastProgressUpdateTime >= 100) {
                    pdfServiceStore.progress += 1;
                    lastProgressUpdateTime = now;
                    const messageIndex = messages.findIndex(msg => msg.progress === pdfServiceStore.progress);
                    if (messageIndex !== -1) {
                        setProgressText(messages[messageIndex].text);
                    }
                }
            } else if (pdfServiceStore.isLoading) {
                // Show middle messages when progress is 100% but still loading
                // Cycle through messages slowly (every 2 seconds)
                if (now - lastMiddleMessageTimeRef.current >= 2000) {
                    if (middleMessageIndexRef.current < middleMessages.length) {
                        setProgressText(middleMessages[middleMessageIndexRef.current].text);
                        middleMessageIndexRef.current++;
                        lastMiddleMessageTimeRef.current = now;
                    } else {
                        // All messages shown, keep showing last message
                        setProgressText(middleMessages[middleMessages.length - 1].text);
                    }
                }
            }
        }, 100); // Check every 100ms, but only update based on timing

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfServiceStore.isLoading, pdfServiceStore.status]);

    // Handle the sequence: Spinner → SVG Loader → Completion
    useEffect(() => {
        // If PDF is completed, show completion immediately regardless of progress
        if (pdfServiceStore.status === 'complete') {
            setShowSvgLoader(false);
            setShowCompletion(true);
            setProgressText("Completed!");
            // Set progress to 100 to ensure UI shows completion
            if (pdfServiceStore.progress < 100) {
                pdfServiceStore.progress = 100;
            }
            return;
        }

        // If PDF failed, hide everything
        if (pdfServiceStore.status === 'failed') {
            setShowSvgLoader(false);
            setShowCompletion(false);
            return;
        }

        if (pdfServiceStore.progress >= 100) {
            // Show SVG loader when progress reaches 100%
            setShowSvgLoader(true);
            // Don't override progress text here - let the message logic handle it
            
            // If loading is complete, hide loader and show completion immediately
            if (!pdfServiceStore.isLoading) {
                // Hide loader and show completion at the same time
                setShowSvgLoader(false);
                setShowCompletion(true);
                setProgressText("Completed!");
            } else {
                // Still loading, keep completion hidden
                setShowCompletion(false);
            }
        } else {
            setShowSvgLoader(false);
            setShowCompletion(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfServiceStore.progress, pdfServiceStore.isLoading, pdfServiceStore.status]);


    return (
        <div className="pdf-progress-container d-flex align-items-center justify-content-center py-3 mt-4">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-auto text-center pt-3">
                        {/* Show completion immediately if PDF is complete, regardless of progress */}
                        {pdfServiceStore.status === 'complete' && !pdfServiceStore.isLoading ? (
                            <>
                                <i className="fas fa-check-circle fa-3x animated-icon" style={{ color: '#6fff00fa', fontSize: '7rem' }}></i>
                                <h6 className="mt-2 progress-text text-white">Completed!</h6>
                                {
                                    !auth.isLoggedIn() && (
                                        <>
                                            <div className='text-center'>
                                                <span className='text-secondary responsive-text'>
                                                    <span className=' text-secondary d-block responsive-text'>
                                                    The link will expire in 30 minutes. To keep your files stored on the server, consider upgrading to our paid services.  <a href='auth/sign-up' className='mx-2' >
                                                    Sign up now!
                                                        </a>
                                                    </span>
                                                    {/* Add spacing below NewsLetter only on mobile */}
                                                    <div className="d-block d-md-none mb-3"></div>
                                                    <NewsLetter />
                                                </span>
                                            </div>
                                        </>
                                    )
                                }
                            </>
                        ) : pdfServiceStore.isLoading || showSvgLoader || !showCompletion ? (
                            <>
                                {/* Show spinner when loading and progress < 100, but only if not completed */}
                                {!showSvgLoader && pdfServiceStore.progress < 100 && pdfServiceStore.status !== 'complete' && (
                                    <>
                                        <Spinner size="7rem" color="danger" />
                                        {
                                            showProgressText && (
                                                <>
                                                    <span className={`spinner-progress-count ${notify ? 'email_notify' : ''}`}>
                                                        {pdfServiceStore.progress <= 100 ? `${pdfServiceStore.progress}%` : ''}
                                                    </span>
                                                    <h6 className="mt-2 progress-text text-white">{progressText}</h6>
                                                </>
                                            )
                                        }
                                    </>
                                )}

                                {/* Show SVG loader when progress reaches 100% */}
                                {showSvgLoader && <StableSvgLoader />}
                                
                                {/* Progress text - separate from loader to prevent re-mounting */}
                                {showSvgLoader && (
                                    <h6 className="mt-2 progress-text text-white">{progressText}</h6>
                                )}

                                {
                                    notify && <div className='text-center'>
                                        <span className='text-secondary responsive-text'>You'll be notified via email when the PDF is ready. &nbsp; 
                                            <span className=' text-secondary responsive-text'>
                                                 You can continue browsing safely.  <button type="button" className='mx-2 btn btn-link p-0 text-decoration-none' onClick={handleStartOver} >
                                                    Generate new!
                                                </button>
                                            </span>
                                        </span>

                                        <span className='text-secondary d-block text-center p-0 m-0 responsive-text' style={{ fontWeight: 'bold', color: '#ff0000' }}>Note: you may find the generated PDFs in the dashboard.</span>
                                    </div>
                                }

                            </>
                        ) : (<>
                            <i className="fas fa-check-circle fa-3x animated-icon" style={{ color: '#6fff00fa', fontSize: '7rem' }}></i>
                            <h6 className="mt-2 progress-text text-white">Completed!</h6>
                            {
                                !auth.isLoggedIn() && (
                                    <>

                                        <div className='text-center'>
                                            <span className='text-secondary responsive-text'>
                                                <span className=' text-secondary d-block responsive-text'>
                                                The link will expire in 30 minutes. To keep your files stored on the server, consider upgrading to our paid services.  <a href='auth/sign-up' className='mx-2' >
                                                Sign up now!
                                                    </a>
                                                </span>
                                                {/* Add spacing below NewsLetter only on mobile */}
                                                <div className="d-block d-md-none mb-3"></div>
                                                <NewsLetter />
                                            </span>
                                        </div>
                                    </>
                                )
                            }
                        </>)
                        }

                    </div>
                </div>
            </div>
        </div>
    );




}

export default observer(PdfProgress);
